import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subject, map, takeUntil } from 'rxjs';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { CommonAccessTokenService, NotifierService, Util } from '@wm-libs/core';
import { WorkersService } from '../../../../core/services/workers.service';
import { PageEvent } from '@angular/material/paginator';
import { ModifierWorkers, Worker, Workers } from '../../../../core/interfaces/workers.interface';
import { TranslateModule } from '@ngx-translate/core';
import { EmptyDataComponent } from '../../../../core/components/empty-data/empty-data.component';
import { DataTableComponent } from '../../../../core/components/data-table/data-table';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgClass, NgIf, NgFor, AsyncPipe } from '@angular/common';
import { AccountsService } from '../../../../core/services/accounts.service';

@Component({
  selector: 'app-workers',
  templateUrl: './workers.component.html',
  styleUrls: ['./workers.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    NgFor,
    MatOptionModule,
    MatButtonModule,
    DataTableComponent,
    EmptyDataComponent,
    AsyncPipe,
    TranslateModule,
  ],
})
export class WorkersComponent implements OnInit, OnDestroy {
  @Input() isStatisticsPage = false;

  // subscribtions
  private _unsubscribe$: Subject<void> = new Subject<void>();

  // data
  initWorkers: Workers | null = null;
  workers: ModifierWorkers | null = null;

  initWorkersLoaded = false;
  workersLoaded = false;

  // forms and filters
  filtersForm!: FormGroup;
  displayedColumns = ['name', 'hashrate', 'hashrate1h', 'hashrate24h', 'lastbeat', 'reject', 'status'];
  statusFilters = [
    { value: 'any', label: 'WORKERS.FILTER.ANY' },
    { value: 'active', label: 'WORKERS.FILTER.ACTIVE' },
    { value: 'inactive', label: 'WORKERS.FILTER.INACTIVE' },
    { value: 'unstable', label: 'WORKERS.FILTER.UNSTABLE' },
    { value: 'dead', label: 'WORKERS.FILTER.DEAD' },
  ];

  // pagination
  pagination = {
    page: 0,
    count: 10,
  };
  // sort
  orderBy!: 'name' | 'hashrate' | 'hashrate1h' | 'hashrate24h' | undefined;
  orderType!: 'asc' | 'desc' | undefined;

  // ui
  isMobile$ = this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(map((result) => result.matches));
  isTablet$ = this.breakpointObserver.observe([Breakpoints.Tablet, Breakpoints.Medium]).pipe(map((result) => result.matches));

  isAccessTokenPage = false;

  constructor(
    public dialog: MatDialog,
    private workersService: WorkersService,
    private formBuilder: FormBuilder,
    private breakpointObserver: BreakpointObserver,
    private notifier: NotifierService,
    private accessTokenService: CommonAccessTokenService,
    private accountsService: AccountsService
  ) {
    this.isAccessTokenPage = this.accessTokenService.isAccessTokenPage();
    this.filtersForm = this.formBuilder.group({
      status: ['any'],
    });
  }

  openDialog() {
    this.accountsService.connect().subscribe();
  }

  ngOnInit() {
    this.notifier.notifications$.pipe(takeUntil(this._unsubscribe$)).subscribe((result) => {
      if (result === 'POOL.FARM_SWITCHED') {
        this.notifier.clearNotifications();
        this.initWorkers = null;
        this.initWorkersLoaded = false;

        this.resetParams();
        // there is no need to call getWorkers().
        // after resetting the params (i.e., changing the form); it will happen automatically.
      }
    });

    this.filtersForm.valueChanges.pipe(takeUntil(this._unsubscribe$)).subscribe(() => {
      this.pagination.page = 0;
      this.getWorkers();
    });

    this.getWorkers();
  }

  getWorkers() {
    this.workers = null;
    this.workersLoaded = false;

    const { status } = this.filtersForm.value;
    const finalStatus = status !== 'any' ? status : undefined;

    const params = {
      from: this.pagination.page,
      count: this.pagination.count,
      status: finalStatus,
      orderBy: this.orderBy,
      orderType: this.orderType,
    };

    this.workersService.getWorkers(params).subscribe((res) => {
      this.workersLoaded = true;

      if (this.initWorkersLoaded === false) {
        this.workersService.setInitWorkers(res);
        this.initWorkers = res;
        this.initWorkersLoaded = true;
      }

      this.updateTable(res);
    });
  }

  sortData(sort: Sort) {
    this.orderBy = sort.active as 'name' | 'hashrate' | 'hashrate1h' | 'hashrate24h';
    this.orderType = sort.direction as 'asc' | 'desc';

    this.getWorkers();
  }

  pageChange(event: PageEvent) {
    this.pagination.count = event.pageSize;
    this.pagination.page = event.pageIndex * this.pagination.count;

    this.getWorkers();
  }

  resetParams() {
    this.orderBy = undefined;
    this.orderType = undefined;
    this.pagination.count = 10;
    this.pagination.page = 0;

    this.filtersForm.patchValue({ status: 'any' });
  }

  updateTable(result: Workers) {
    this.workers = {
      summary: result?.summary || 0,
      count: result?.count || 0,
      data: result?.data?.map((item: Worker) => ({
        ...item,
        lastbeat: Util.formatDate(new Date(Number(item.lastbeat) * 1000)),
        hashrate: item.hashrate + ' ' + item.hashrateSymbol,
        hashrate1h: item.hashrate1h + ' ' + item.hashrate1hSymbol,
        hashrate24h: item.hashrate24h + ' ' + item.hashrate24hSymbol,
        status: `WORKERS.FILTER.${item.status.toUpperCase()}`,
      })),
    };
  }

  ngOnDestroy() {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
